import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AssessmentOutlined,
  BarChartOutlined,
  DownloadOutlined,
  ErrorOutline,
  InfoOutlined,
  Inventory2Outlined,
  ReceiptLongOutlined,
  StorefrontOutlined,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const GILROY_MEDIUM = "gilroyMedium";
const GILROY_BOLD = "GilroyBold";
const PDF_EXPORT_WIDTH = 1280;
const PDF_EXPORT_MIN_HEIGHT = 900;

const T = {
  text: "#0f172a",
  muted: "#64748b",
  soft: "#f8fafc",
  panel: "#ffffff",
  border: "#e2e8f0",
  borderStrong: "#cbd5e1",
  blue: "#2563eb",
  cyan: "#0891b2",
  green: "#16a34a",
  amber: "#d97706",
  red: "#dc2626",
  purple: "#7c3aed",
  indigo: "#4f46e5",
  slate: "#475569",
};

const SERIES = [T.blue, T.green, T.amber, T.red, T.purple, T.cyan, T.indigo];

const baseTextSx = {
  fontFamily: GILROY_MEDIUM,
  fontSize: 13,
};

const chartTickSx = {
  fill: T.muted,
  fontSize: 13,
  fontFamily: GILROY_MEDIUM,
};

const controlButtonSx = {
  textTransform: "none",
  borderRadius: 2,
  fontFamily: GILROY_MEDIUM,
  fontSize: 13,
  minHeight: 32,
  px: 1.45,
  boxShadow: "none",
  borderColor: "#c7d2fe",
  "&.MuiButton-contained": {
    background: `linear-gradient(135deg, ${T.blue} 0%, ${T.cyan} 100%)`,
    boxShadow: "0 10px 18px rgba(37,99,235,0.18)",
  },
  "&.MuiButton-outlined": {
    backgroundColor: "#ffffff",
    color: T.slate,
  },
  "&:hover": {
    boxShadow: "0 12px 22px rgba(15,23,42,0.12)",
  },
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.data)) return value.data;
  if (Array.isArray(value.results)) return value.results;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.rows)) return value.rows;
  return [];
};

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const getPeriodRows = (value, mode = "monthly") => {
  if (Array.isArray(value)) return value;
  const objectValue = asObject(value);
  return asArray(
    objectValue[mode] ||
      objectValue[mode?.toLowerCase?.()] ||
      objectValue.monthly ||
      objectValue.weekly ||
      objectValue.yearly,
  );
};

const formatNumber = (value, options = {}) =>
  toNumber(value).toLocaleString("en-IN", options);

const formatCurrency = (value) =>
  `\u20B9 ${formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatCompactCurrency = (value) => {
  const number = toNumber(value);
  if (Math.abs(number) >= 10000000) {
    return `\u20B9 ${(number / 10000000).toFixed(1)} Cr`;
  }
  if (Math.abs(number) >= 100000) {
    return `\u20B9 ${(number / 100000).toFixed(1)} L`;
  }
  return formatCurrency(number);
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "NA";
  return `${formatNumber(value, { maximumFractionDigits: 2 })}%`;
};

const formatPeriod = (period) => {
  if (String(period || "").includes("-W")) return period;
  const [year, month] = String(period || "").split("-");
  if (!year || !month) return period || "-";
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString(
    "en-IN",
    { month: "short", year: "2-digit" },
  );
};

const valueFormatterForKey = (key, value) => {
  const lower = String(key || "").toLowerCase();
  if (lower.includes("value") || lower.includes("amount") || lower.includes("price")) {
    return formatCurrency(value);
  }
  if (lower.includes("percentage") || lower.includes("rate") || lower.includes("utilization")) {
    return formatPercent(value);
  }
  return formatNumber(value);
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <Paper
      elevation={4}
      sx={{
        p: 1.4,
        border: `1px solid ${T.border}`,
        borderRadius: 2,
        minWidth: 210,
        fontFamily: GILROY_MEDIUM,
        boxShadow: "0 18px 36px rgba(15,23,42,0.16)",
      }}
    >
      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 13, color: T.text, mb: 0.8 }}>
        {label}
      </Typography>
      <Stack spacing={0.45}>
        {payload.map((entry) => (
          <Stack key={`${entry.dataKey}-${entry.name}`} direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: entry.color }} />
            <Typography sx={{ ...baseTextSx, color: T.muted }}>
              {entry.name}: {valueFormatterForKey(entry.dataKey || entry.name, entry.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
      {payload[0]?.payload?.tooltipNote ? (
        <Typography sx={{ ...baseTextSx, color: T.muted, mt: 0.8 }}>
          {payload[0].payload.tooltipNote}
        </Typography>
      ) : null}
    </Paper>
  );
};

const InfoTip = ({ title }) => (
  <Tooltip
    title={title}
    arrow
    slotProps={{ tooltip: { sx: { fontSize: 13, fontFamily: GILROY_MEDIUM, maxWidth: 420 } } }}
  >
    <InfoOutlined sx={{ fontSize: 16, color: T.muted }} />
  </Tooltip>
);

const SectionCard = ({ title, subtitle, icon, children, action, height }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      borderRadius: 2.25,
      borderColor: T.border,
      background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)",
      minHeight: height || "auto",
      height: "100%",
      boxShadow: "0 16px 34px rgba(15,23,42,0.06)",
      transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        borderColor: T.borderStrong,
        boxShadow: "0 20px 44px rgba(15,23,42,0.09)",
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.6 }}>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {icon ? <Box sx={{ color: T.blue, display: "flex" }}>{icon}</Box> : null}
          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 15, color: T.text }}>
            {title}
          </Typography>
        </Stack>
        {subtitle ? (
          <Typography sx={{ ...baseTextSx, color: T.muted, mt: 0.35 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Paper>
);

const MetricTooltip = ({ rows = [] }) => (
  <Stack spacing={0.7}>
    {rows.map((row) => (
      <Stack key={row.label} direction="row" justifyContent="space-between" spacing={2}>
        <Typography sx={{ ...baseTextSx, color: T.muted }}>{row.label}</Typography>
        <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 13, color: T.text }}>{row.value}</Typography>
      </Stack>
    ))}
  </Stack>
);

const MetricCard = ({ label, value, note, accent = T.blue, icon, tooltip }) => (
  <Tooltip
    title={tooltip || ""}
    arrow
    placement="bottom-start"
    disableHoverListener={!tooltip}
    slotProps={{
      tooltip: {
        sx: {
          bgcolor: T.panel,
          color: T.text,
          border: `1px solid ${T.border}`,
          boxShadow: "0 18px 36px rgba(15,23,42,0.16)",
          borderRadius: 2,
          p: 1.4,
          maxWidth: 440,
        },
      },
      arrow: { sx: { color: T.panel } },
    }}
  >
    <Paper
      variant="outlined"
      sx={{
        p: 1.8,
        borderRadius: 2.25,
        borderColor: `${accent}33`,
        background: `linear-gradient(145deg, ${accent}16 0%, #ffffff 58%, ${accent}08 100%)`,
        minHeight: 126,
        height: "100%",
        boxShadow: "0 14px 30px rgba(15,23,42,0.07)",
        cursor: tooltip ? "help" : "default",
        transition: "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          borderColor: `${accent}66`,
          boxShadow: "0 20px 38px rgba(15,23,42,0.1)",
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ ...baseTextSx, color: T.muted }}>
            {label}
          </Typography>
          <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: { xs: 20, md: 23 }, color: T.text, mt: 0.8, lineHeight: 1.1 }}>
            {value}
          </Typography>
          {note ? (
            <Typography sx={{ ...baseTextSx, color: T.muted, mt: 0.7 }}>
              {note}
            </Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            background: `linear-gradient(135deg, ${accent}1f 0%, #ffffff 100%)`,
            border: `1px solid ${accent}24`,
            color: accent,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  </Tooltip>
);

const EmptyPanel = ({ label = "No data available" }) => (
  <Box
    sx={{
      minHeight: 180,
      display: "grid",
      placeItems: "center",
      border: `1px dashed ${T.border}`,
      borderRadius: 2,
      color: T.muted,
      fontFamily: GILROY_MEDIUM,
      fontSize: 13,
      backgroundColor: T.soft,
    }}
  >
    {label}
  </Box>
);

const PayAsYouGoDashboard = ({ summaryData, loading }) => {
  const dashboardRef = useRef(null);
  const [trendMode, setTrendMode] = useState("monthly");
  const [vendorMode, setVendorMode] = useState("value");
  const [itemMode, setItemMode] = useState("value");
  const [exporting, setExporting] = useState(false);

  const filters = summaryData?.filters_applied || {};
  const availability = summaryData?.data_availability || {};
  const overview = summaryData?.overview || {};
  const upload = overview.upload_processing || {};
  const matching = summaryData?.matching_analytics || {};
  const invoice = summaryData?.invoice_analytics || {};
  const vendor = summaryData?.vendor_analytics || {};
  const po = summaryData?.po_analytics || {};
  const grn = summaryData?.grn_analytics || {};
  const exception = summaryData?.exception_analytics || {};
  const item = summaryData?.item_analytics || {};
  const geo = summaryData?.geographic_analytics || {};
  const trend = summaryData?.trend_analytics || {};

  const activeTrendRows = useMemo(
    () =>
      (getPeriodRows(invoice.invoice_value_trend, trendMode).length
        ? getPeriodRows(invoice.invoice_value_trend, trendMode)
        : getPeriodRows(trend.current_period, trendMode)
      ).map((row) => ({
        rawPeriod: row.period,
        period: trendMode === "monthly" ? formatPeriod(row.period) : String(row.period || "-"),
        invoiceValue: toNumber(row.invoice_value),
        invoiceCount: toNumber(row.invoice_count),
      })),
    [invoice.invoice_value_trend, trend.current_period, trendMode],
  );

  const yearlyTrend = useMemo(() => {
    const yearlyRows = getPeriodRows(invoice.invoice_value_trend, "yearly");
    if (yearlyRows.length) {
      return yearlyRows.map((row) => ({
        rawPeriod: row.period,
        period: String(row.period || row.year || "-"),
        invoiceValue: toNumber(row.invoice_value),
        invoiceCount: toNumber(row.invoice_count),
      }));
    }

    const countByYear = getPeriodRows(invoice.invoice_value_trend, "monthly").reduce((acc, row) => {
      const year = String(row.period || "").slice(0, 4);
      if (year) acc[year] = (acc[year] || 0) + toNumber(row.invoice_count);
      return acc;
    }, {});

    return asArray(trend.previous_two_years).map((row) => ({
      period: String(row.year || "-"),
      invoiceValue: toNumber(row.invoice_value),
      invoiceCount: countByYear[String(row.year)] || 0,
      tooltipNote: "Yearly value is provided by trend_analytics.previous_two_years.",
    }));
  }, [invoice.invoice_value_trend, trend.previous_two_years]);

  const invoiceTrend = trendMode === "yearly" ? yearlyTrend : activeTrendRows;

  const yearlyTooltipRows = yearlyTrend.map((row) => ({
    label: row.period,
    value: `${formatCurrency(row.invoiceValue)} (${formatNumber(row.invoiceCount)} invoices)`,
  }));

  const processedDocumentCounts = asObject(overview.processed_document_counts);
  const extractedDocumentCounts = asObject(upload.extracted_document_counts);

  const documentRows = Object.entries(processedDocumentCounts).map(([name, count]) => ({
    name,
    processed: toNumber(count),
    extracted: toNumber(extractedDocumentCounts[name]),
  }));

  const matchMix = [
    { name: "Matched", value: toNumber(matching.matched_count) },
    { name: "Partial", value: toNumber(matching.partial_count) },
    { name: "Exception", value: toNumber(matching.exception_count) },
    { name: "Incomplete", value: toNumber(matching.incomplete_count) },
  ].filter((row) => row.value > 0);

  const vendorRows =
    vendorMode === "value"
      ? asArray(vendor.top_vendors_by_value)
      : asArray(vendor.top_vendors_by_count);

  const vendorChartRows = vendorRows.map((row) => ({
    label: row.vendor || "Unknown",
    invoiceValue: toNumber(row.invoice_value),
    invoiceCount: toNumber(row.invoice_count),
  }));

  const vendorTrendRows = getPeriodRows(vendor.vendor_trend, trendMode).map((row) => ({
    label: `${trendMode === "monthly" ? formatPeriod(row.period) : row.period || "-"} - ${row.vendor || "Unknown"}`,
    invoiceValue: toNumber(row.invoice_value),
    invoiceCount: toNumber(row.invoice_count),
  }));

  const poRows = asArray(po.po_utilization).length
    ? asArray(po.po_utilization)
    : asArray(po.po_wise_invoice_value);
  const poChartRows = poRows.map((row) => ({
    label: row.po_number || "Unknown",
    utilization: toNumber(row.utilization_percentage),
    invoiceValue: toNumber(row.invoice_value),
    poValue: toNumber(row.po_value),
    invoiceCount: toNumber(row.invoice_count),
  }));

  const exceptionTrendRows = getPeriodRows(exception.exception_trend, trendMode).map((row) => ({
    period: trendMode === "monthly" ? formatPeriod(row.period) : String(row.period || "-"),
    exceptionCount: toNumber(row.exception_count),
  }));

  const vendorExceptionRows = asArray(exception.vendor_wise_exceptions).map((row) => ({
    label: row.vendor || "Unknown",
    exceptionCount: toNumber(row.exception_count),
  }));

  const topItemRows =
    itemMode === "value"
      ? asArray(item.item_wise_purchase_value).length
        ? asArray(item.item_wise_purchase_value)
        : asArray(item.top_purchased_items)
      : itemMode === "quantity"
        ? asArray(item.quantity_by_item)
        : asArray(item.frequently_purchased_items);

  const itemChartRows = topItemRows.slice(0, 12).map((row) => ({
    label: row.item_name || row.part_number || "-",
    purchaseValue: toNumber(row.purchase_value),
    quantity: toNumber(row.quantity),
    purchaseCount: toNumber(row.purchase_count),
  }));

  const topInvoiceChartRows = asArray(invoice.top_invoices).slice(0, 10).map((row) => ({
    label: row.invoice_number || "Unknown",
    invoiceValue: toNumber(row.invoice_value),
    invoiceCount: 1,
  }));

  const grnChartRows = asArray(grn.grn_wise_invoice_value).slice(0, 12).map((row) => ({
    label: row.grn_number || row.invoice_number || "Unknown",
    invoiceValue: toNumber(row.invoice_value),
  }));

  const multipleInvoiceRows = asArray(po.multiple_invoices_per_po).map((row) => ({
    label: row.po_number || "Unknown",
    invoiceValue: toNumber(row.invoice_value),
    invoiceCount: toNumber(row.invoice_count),
    utilization: toNumber(row.utilization_percentage),
  }));

  const multipleGrnRows = asArray(grn.multiple_grns_per_po).map((row) => ({
    label: row.po_number || "Unknown",
    grnCount: toNumber(row.grn_count),
  }));

  const poGrnVarianceRows = asArray(exception.po_grn_quantity_variances).map((row) => ({
    label: row.part_number || row.match_code || "Unknown",
    difference: toNumber(row.difference),
  }));

  const invoiceGrnVarianceRows = asArray(exception.invoice_grn_quantity_variances).map((row) => ({
    label: row.part_number || row.match_code || "Unknown",
    difference: toNumber(row.difference),
  }));

  const unitPriceCoverageRows = asArray(item.unit_price_trend).slice(0, 12).map((row) => {
    const prices = asArray(row.prices);
    const latest = prices[0] || {};
    return {
      label: row.item_name || row.part_number || "Unknown",
      observations: prices.length,
      unitPrice: toNumber(latest.rate),
    };
  });

  const availabilityRows = [
    {
      metric: "Currency",
      status: availability.currency?.available ? "Available" : "Not available",
      detail: `${formatNumber(availability.currency?.populated_invoice_count)} / ${formatNumber(availability.currency?.total_invoice_count)} populated`,
    },
    {
      metric: "Geography",
      status: availability.geography?.available ? "Available" : "Not available",
      detail: `${formatNumber(availability.geography?.populated_invoice_count)} / ${formatNumber(availability.geography?.total_invoice_count)} populated`,
    },
    {
      metric: "Vendor age classification",
      status: availability.vendor_age_classification?.available ? "Available" : "Not available",
      detail: availability.vendor_age_classification?.reason || "-",
    },
    {
      metric: "Geographic analytics",
      status: geo.available ? "Available" : "Not available",
      detail: geo.reason || "-",
    },
  ];

  const appliedFilters = Object.entries(filters)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key.replace(/_/g, " ")}: ${value}`);

  const exportDashboardAsPDF = async () => {
    const exportRoot = dashboardRef.current;
    if (!exportRoot) return;

    try {
      setExporting(true);
      await new Promise((resolve) => setTimeout(resolve, 250));
      try {
        if (document.fonts?.ready) await document.fonts.ready;
      } catch (_) {}
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)),
      );

      const rootWidth = Math.max(
        PDF_EXPORT_WIDTH,
        Math.ceil(exportRoot.scrollWidth || exportRoot.offsetWidth || 0),
      );
      const rootHeight = Math.max(
        1,
        Math.ceil(exportRoot.scrollHeight || exportRoot.offsetHeight || 0),
      );
      const canvas = await html2canvas(exportRoot, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: rootWidth,
        height: rootHeight,
        windowWidth: Math.max(rootWidth, PDF_EXPORT_WIDTH),
        windowHeight: Math.max(rootHeight, PDF_EXPORT_MIN_HEIGHT),
        ignoreElements: (el) =>
          !!(
            el?.hasAttribute?.("data-export-ignore") ||
            el?.getAttribute?.("data-export-ignore") === "true"
          ),
        onclone: (clonedDoc) => {
          const clonedRoot = clonedDoc.querySelector(
            '[data-pay-as-you-go-dashboard-export-root="true"]',
          );
          if (clonedRoot) {
            clonedRoot.style.width = `${PDF_EXPORT_WIDTH}px`;
            clonedRoot.style.maxWidth = "none";
            clonedRoot.style.overflow = "visible";
          }
          clonedDoc.documentElement.style.width = `${PDF_EXPORT_WIDTH}px`;
          clonedDoc.body.style.width = `${PDF_EXPORT_WIDTH}px`;
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const margin = 8;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;
      const pageHeightPx = Math.max(
        1,
        Math.floor((usableHeight * canvas.width) / usableWidth),
      );
      const sliceCanvas = document.createElement("canvas");
      const sliceCtx = sliceCanvas.getContext("2d");
      let sourceY = 0;
      let hasRendered = false;

      while (sliceCtx && sourceY < canvas.height) {
        const sliceHeight = Math.min(pageHeightPx, canvas.height - sourceY);
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeight;
        sliceCtx.fillStyle = "#ffffff";
        sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        sliceCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          sliceHeight,
          0,
          0,
          canvas.width,
          sliceHeight,
        );

        const imageData = sliceCanvas.toDataURL("image/jpeg", 0.95);
        const imageHeight = (sliceHeight * usableWidth) / canvas.width;
        if (hasRendered) pdf.addPage();
        pdf.addImage(imageData, "JPEG", margin, margin, usableWidth, imageHeight);
        hasRendered = true;
        sourceY += sliceHeight;
      }

      if (hasRendered) {
        pdf.save("Pay-As-You-Go-Invoice-Automation-Dashboard.pdf");
      }
    } catch (error) {
      console.error("Pay as you go dashboard PDF export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 3 }}>
        <Skeleton variant="rounded" height={150} sx={{ borderRadius: 3 }} />
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {[1, 2, 3, 4].map((key) => (
            <Grid item xs={12} md={6} key={key}>
              <Skeleton variant="rounded" height={300} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!summaryData) return null;

  return (
    <Box
      sx={{
        width: "100%",
        mt: 3,
        p: { xs: 1, md: 1.5 },
        borderRadius: 3,
        background:
          "linear-gradient(135deg, #f8fbff 0%, #f1f7ff 42%, #f8fafc 100%)",
      }}
    >
      <Paper
        ref={dashboardRef}
        data-pay-as-you-go-dashboard-export-root="true"
        variant="outlined"
        sx={{
          p: { xs: 2, md: 2.75 },
          borderRadius: 3,
          borderColor: T.border,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(8px)",
          boxShadow: "0 22px 52px rgba(15,23,42,0.08)",
          ...(exporting
            ? {
                width: `${PDF_EXPORT_WIDTH}px`,
                maxWidth: "none",
                overflow: "visible",
              }
            : {}),
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <AssessmentOutlined sx={{ color: T.blue, fontSize: 25 }} />
              <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: { xs: 17, md: 20 }, color: T.text }}>
                Pay As You Go Invoice Automation Dashboard
              </Typography>
              <InfoTip title="The dashboard is driven by get_bulk_twm_summary and includes saved bulk matching submissions, reprocessed submissions, upload processing, invoice analytics, vendor analytics, PO/GRN analytics, exceptions, items, geography and trend analytics." />
            </Stack>
            
          </Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            justifyContent={{ xs: "flex-start", md: "flex-end" }}
            data-export-ignore
          >
            <Chip label={`Periods: ${asArray(filters.periods_returned).join(", ") || filters.period || "MONTHLY"}`} size="large" sx={{ ...baseTextSx, fontSize: 14, backgroundColor: "#eef6ff", border: `1px solid ${T.border}` }} />
            <Chip label={`Analytics basis: ${overview.analytics_date_basis || "-"}`} size="large" sx={{ ...baseTextSx, fontSize: 14, backgroundColor: "#f0fdf4", border: `1px solid #bbf7d0` }} />
            <Chip label={`Upload basis: ${upload.date_basis || "-"}`} size="large" sx={{ ...baseTextSx, fontSize: 14, backgroundColor: "#fff7ed", border: `1px solid #fed7aa` }} />
            <Button
              variant="contained"
              startIcon={<DownloadOutlined sx={{ fontSize: "18px !important" }} />}
              onClick={exportDashboardAsPDF}
              disabled={exporting || !summaryData}
              sx={{
                minHeight: 40,
                borderRadius: 2,
                fontFamily: GILROY_BOLD,
                fontSize: 14,
                textTransform: "none",
                boxShadow: "none",
                background: `linear-gradient(135deg, ${T.blue} 0%, ${T.cyan} 100%)`,
                "&:hover": {
                  boxShadow: "0 10px 20px rgba(37,99,235,0.2)",
                },
              }}
            >
              {exporting ? "Exporting..." : "Export PDF"}
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              label="Total Invoice Value"
              value={formatCompactCurrency(overview.total_invoice_value)}
              note={`${formatNumber(overview.total_invoices)} invoices, avg ${formatCompactCurrency(overview.average_invoice_value)}`}
              icon={<ReceiptLongOutlined />}
              accent={T.blue}
              tooltip={<MetricTooltip rows={[
                { label: "Total", value: formatCurrency(overview.total_invoice_value) },
                { label: "Highest", value: formatCurrency(overview.highest_invoice_value) },
                { label: "Lowest", value: formatCurrency(overview.lowest_invoice_value) },
                ...yearlyTooltipRows,
              ]} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              label="Documents Processed"
              value={formatNumber(overview.total_documents_processed)}
              note={`${formatNumber(overview.total_batches)} matching batches, ${formatNumber(upload.total_batches_received)} uploads`}
              icon={<Inventory2Outlined />}
              accent={T.cyan}
              tooltip={<MetricTooltip rows={[
                { label: "Processed invoices", value: formatNumber(processedDocumentCounts.INVOICE) },
                { label: "Processed POs", value: formatNumber(processedDocumentCounts.PO) },
                { label: "Processed GRNs", value: formatNumber(processedDocumentCounts.GRN) },
                { label: "Extracted documents", value: formatNumber(upload.total_documents_extracted) },
              ]} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              label="Three Way Success"
              value={formatPercent(matching.three_way_success_rate)}
              note={`${formatNumber(matching.three_way_count)} three-way groups from ${formatNumber(matching.total_matches)} matches`}
              icon={<TrendingUp />}
              accent={T.green}
              tooltip={<MetricTooltip rows={[
                { label: "Three-way success", value: formatPercent(matching.three_way_success_rate) },
                { label: "Two-way success", value: formatPercent(matching.two_way_success_rate) },
                { label: "Matched", value: formatNumber(matching.matched_count) },
                { label: "Partial", value: formatNumber(matching.partial_count) },
                { label: "Incomplete", value: formatNumber(matching.incomplete_count) },
              ]} />}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricCard
              label="Exceptions"
              value={formatNumber(matching.exception_count)}
              note={`${formatNumber(exception.tolerance_breach_count)} tolerance breaches`}
              icon={<ErrorOutline />}
              accent={T.red}
              tooltip={<MetricTooltip rows={[
                { label: "Missing PO", value: formatNumber(exception.missing_po_count) },
                { label: "Missing GRN", value: formatNumber(exception.missing_grn_count) },
                { label: "PO vs GRN variance", value: formatNumber(asArray(exception.po_grn_quantity_variances).length) },
                { label: "Invoice vs GRN variance", value: formatNumber(asArray(exception.invoice_grn_quantity_variances).length) },
              ]} />}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} lg={8}>
            <SectionCard
              title="Invoice Value Trend"
              subtitle="Monthly detail and yearly comparison from trend analytics"
              icon={<BarChartOutlined />}
              height={360}
              action={
                <Stack direction="row" spacing={1}>
                  {[
                    ["weekly", "Weekly"],
                    ["monthly", "Monthly"],
                    ["yearly", "Yearly"],
                  ].map(([value, label]) => (
                    <Button
                      key={value}
                      size="small"
                      variant={trendMode === value ? "contained" : "outlined"}
                      onClick={() => setTrendMode(value)}
                      sx={controlButtonSx}
                    >
                      {label}
                    </Button>
                  ))}
                </Stack>
              }
            >
              {invoiceTrend.length ? (
                <Box sx={{ height: 270 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={invoiceTrend} margin={{ top: 10, right: 18, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="paygInvoiceArea" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={T.blue} stopOpacity={0.34} />
                          <stop offset="100%" stopColor={T.blue} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#dbeafe" />
                      <XAxis dataKey="period" tick={chartTickSx} />
                      <YAxis yAxisId="value" tickFormatter={formatCompactCurrency} tick={chartTickSx} width={74} />
                      <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={chartTickSx} width={40} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Area yAxisId="value" type="monotone" dataKey="invoiceValue" name="Invoice Value" stroke={T.blue} strokeWidth={3} fill="url(#paygInvoiceArea)" />
                      <Line yAxisId="count" type="monotone" dataKey="invoiceCount" name="Invoice Count" stroke={T.purple} strokeWidth={2.5} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyPanel />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <SectionCard title="Matching Mix" subtitle={`${formatNumber(matching.total_matches)} total matches`} icon={<AssessmentOutlined />} height={360}>
              {matchMix.length ? (
                <Box sx={{ height: 270, position: "relative" }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={matchMix} dataKey="value" nameKey="name" innerRadius={68} outerRadius={104} paddingAngle={4} cornerRadius={8}>
                        {matchMix.map((entry, index) => (
                          <Cell key={entry.name} fill={SERIES[index % SERIES.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Stack spacing={0.65} sx={{ position: "absolute", right: 0, bottom: 0 }}>
                    {matchMix.map((row, index) => (
                      <Typography key={row.name} sx={{ ...baseTextSx, color: T.muted }}>
                        <Box component="span" sx={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", mr: 0.75, backgroundColor: SERIES[index % SERIES.length] }} />
                        {row.name}: {formatNumber(row.value)}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <EmptyPanel />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Vendors"
              subtitle={`Top 10 concentration: ${formatPercent(vendor.vendor_concentration?.top_10_percentage)}`}
              icon={<StorefrontOutlined />}
              height={370}
              action={
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant={vendorMode === "value" ? "contained" : "outlined"} onClick={() => setVendorMode("value")} sx={controlButtonSx}>Value</Button>
                  <Button size="small" variant={vendorMode === "count" ? "contained" : "outlined"} onClick={() => setVendorMode("count")} sx={controlButtonSx}>Count</Button>
                </Stack>
              }
            >
              {vendorChartRows.length ? (
                <Box sx={{ height: 275 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={vendorChartRows} layout="vertical" margin={{ top: 8, right: 22, left: 30, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={vendorMode === "value" ? formatCompactCurrency : formatNumber} tick={chartTickSx} />
                      <YAxis type="category" dataKey="label" width={150} tick={chartTickSx} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey={vendorMode === "value" ? "invoiceValue" : "invoiceCount"} name={vendorMode === "value" ? "Invoice Value" : "Invoice Count"} fill={T.cyan} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyPanel />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="PO Utilization" subtitle={`${formatNumber(po.fully_invoiced_count)} fully, ${formatNumber(po.partially_invoiced_count)} partial, ${formatNumber(po.over_invoiced_count)} over invoiced`} icon={<Inventory2Outlined />} height={370}>
              {poChartRows.length ? (
                <Box sx={{ height: 275 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={poChartRows} margin={{ top: 8, right: 18, left: 0, bottom: 56 }}>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" angle={-22} textAnchor="end" height={70} tick={chartTickSx} />
                      <YAxis tick={chartTickSx} width={42} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="utilization" name="Utilization %" fill={T.green} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyPanel />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Exception Signals" subtitle="Trend and vendor-wise exception pressure" icon={<ErrorOutline />} height={360}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {exceptionTrendRows.length ? (
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={exceptionTrendRows} margin={{ top: 8, right: 10, left: 0, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#fee2e2" />
                          <XAxis dataKey="period" tick={chartTickSx} />
                          <YAxis allowDecimals={false} tick={chartTickSx} width={38} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Line dataKey="exceptionCount" name="Exception Count" stroke={T.red} strokeWidth={3} dot={{ r: 4 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} md={6}>
                  {vendorExceptionRows.length ? (
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vendorExceptionRows} layout="vertical" margin={{ top: 8, right: 10, left: 24, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#fee2e2" />
                          <XAxis type="number" allowDecimals={false} tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={120} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="exceptionCount" name="Exception Count" fill={T.red} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Document Processing" subtitle="Processed document counts and extraction volume" icon={<ReceiptLongOutlined />} height={360}>
              {documentRows.length ? (
                <Box sx={{ height: 245 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={documentRows} margin={{ top: 10, right: 18, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={chartTickSx} />
                      <YAxis allowDecimals={false} tick={chartTickSx} width={42} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="processed" name="Processed Documents" fill={T.indigo} radius={[8, 8, 0, 0]} />
                      <Bar dataKey="extracted" name="Extracted Documents" fill={T.amber} radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <EmptyPanel />
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              title="Item Analytics"
              subtitle="Item-wise value, quantity, purchase frequency and unit-price coverage"
              icon={<Inventory2Outlined />}
              action={
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {[
                    ["value", "Value"],
                    ["quantity", "Quantity"],
                    ["frequency", "Frequency"],
                  ].map(([value, label]) => (
                    <Button key={value} size="medium" variant={itemMode === value ? "contained" : "outlined"} onClick={() => setItemMode(value)} sx={controlButtonSx}>{label}</Button>
                  ))}
                </Stack>
              }
            >
              <Grid container spacing={2}>
                <Grid item xs={12} lg={6}>
                  {itemChartRows.length ? (
                    <Box sx={{ height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={itemChartRows} layout="vertical" margin={{ top: 8, right: 18, left: 50, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                          <XAxis type="number" tickFormatter={itemMode === "value" ? formatCompactCurrency : formatNumber} tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={190} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey={itemMode === "value" ? "purchaseValue" : itemMode === "quantity" ? "quantity" : "purchaseCount"} name={itemMode === "value" ? "Purchase Value" : itemMode === "quantity" ? "Quantity" : "Purchase Count"} fill={T.purple} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} lg={6}>
                  {unitPriceCoverageRows.length ? (
                    <Box sx={{ height: 320 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={unitPriceCoverageRows} layout="vertical" margin={{ top: 8, right: 18, left: 50, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                          <XAxis type="number" tickFormatter={formatNumber} tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={190} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="unitPrice" name="Unit Price" fill={T.cyan} radius={[0, 8, 8, 0]} />
                          <Bar dataKey="observations" name="Price Observations" fill={T.amber} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Top 10 Invoices" subtitle="Highest invoice values in the selected data" icon={<ReceiptLongOutlined />}>
              {topInvoiceChartRows.length ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topInvoiceChartRows} layout="vertical" margin={{ top: 8, right: 18, left: 35, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={formatCompactCurrency} tick={chartTickSx} />
                      <YAxis type="category" dataKey="label" width={155} tick={chartTickSx} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="invoiceValue" name="Invoice Value" fill={T.blue} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : <EmptyPanel />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="PO Detail" subtitle="PO-wise invoice value and utilization status" icon={<Inventory2Outlined />}>
              {poChartRows.length ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={poChartRows} margin={{ top: 10, right: 18, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" angle={-24} textAnchor="end" height={72} tick={chartTickSx} />
                      <YAxis yAxisId="value" tickFormatter={formatCompactCurrency} tick={chartTickSx} width={72} />
                      <YAxis yAxisId="percent" orientation="right" tickFormatter={formatPercent} tick={chartTickSx} width={58} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar yAxisId="value" dataKey="poValue" name="PO Value" fill={T.slate} radius={[8, 8, 0, 0]} />
                      <Bar yAxisId="value" dataKey="invoiceValue" name="Invoice Value" fill={T.blue} radius={[8, 8, 0, 0]} />
                      <Line yAxisId="percent" type="monotone" dataKey="utilization" name="Utilization %" stroke={T.green} strokeWidth={3} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : <EmptyPanel />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="GRN Analytics" subtitle={`${formatNumber(grn.complete_grn_count)} complete, ${formatNumber(grn.partial_grn_count)} partial GRNs`} icon={<Inventory2Outlined />}>
              {grnChartRows.length ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grnChartRows} layout="vertical" margin={{ top: 8, right: 18, left: 35, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={formatCompactCurrency} tick={chartTickSx} />
                      <YAxis type="category" dataKey="label" width={155} tick={chartTickSx} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="invoiceValue" name="Invoice Value" fill={T.cyan} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : <EmptyPanel />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Multiple Documents" subtitle="Multiple invoices per PO and multiple GRNs per PO" icon={<BarChartOutlined />}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  {multipleInvoiceRows.length ? (
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={multipleInvoiceRows} margin={{ top: 8, right: 18, left: 0, bottom: 54 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="label" angle={-20} textAnchor="end" height={62} tick={chartTickSx} />
                          <YAxis tick={chartTickSx} width={42} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="invoiceCount" name="Invoice Count" fill={T.blue} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} md={5}>
                  {multipleGrnRows.length ? (
                    <Box sx={{ height: 240 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={multipleGrnRows} margin={{ top: 8, right: 12, left: 0, bottom: 54 }}>
                          <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                          <XAxis dataKey="label" angle={-20} textAnchor="end" height={62} tick={chartTickSx} />
                          <YAxis allowDecimals={false} tick={chartTickSx} width={38} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="grnCount" name="GRN Count" fill={T.green} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Variance And Exceptions" subtitle="Quantity variances, missing documents and vendor exception counts" icon={<ErrorOutline />}>
              <Grid container spacing={2}>
                <Grid item xs={12} lg={4}>
                  {poGrnVarianceRows.length ? (
                    <Box sx={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={poGrnVarianceRows} layout="vertical" margin={{ top: 8, right: 14, left: 24, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#fee2e2" />
                          <XAxis type="number" tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={120} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="difference" name="PO-GRN Difference" fill={T.red} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} lg={4}>
                  {invoiceGrnVarianceRows.length ? (
                    <Box sx={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={invoiceGrnVarianceRows} layout="vertical" margin={{ top: 8, right: 14, left: 24, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#fee2e2" />
                          <XAxis type="number" tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={120} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="difference" name="Invoice-GRN Difference" fill={T.amber} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} lg={4}>
                  {vendorExceptionRows.length ? (
                    <Box sx={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vendorExceptionRows} layout="vertical" margin={{ top: 8, right: 14, left: 24, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#fee2e2" />
                          <XAxis type="number" allowDecimals={false} tick={chartTickSx} />
                          <YAxis type="category" dataKey="label" width={120} tick={chartTickSx} />
                          <RechartsTooltip content={<ChartTooltip />} />
                          <Bar dataKey="exceptionCount" name="Exception Count" fill={T.purple} radius={[0, 8, 8, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  ) : <EmptyPanel />}
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 13, color: T.text, mb: 1 }}>Price Variances</Typography>
                  <Box sx={{ display: "grid", placeItems: "center", minHeight: 120, borderRadius: 2, backgroundColor: T.soft, border: `1px solid ${T.border}` }}>
                    <Typography sx={{ fontFamily: GILROY_BOLD, color: T.text }}>{formatNumber(asArray(exception.price_variances).length)} records</Typography>
                    <Typography sx={{ ...baseTextSx, color: T.muted }}>Price variance entries returned by API</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} lg={6}>
                  <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 13, color: T.text, mb: 1 }}>Quantity Variance Summary</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(asObject(exception.quantity_variances)).map(([metric, value]) => (
                      <Chip
                        key={metric}
                        label={`${metric.replace(/_/g, " ")}: ${formatNumber(asArray(value).length)}`}
                        sx={{ fontFamily: GILROY_BOLD, textTransform: "capitalize", fontSize: 13, backgroundColor: T.soft, borderColor: T.border, color: T.text }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Vendor Trend" subtitle="Vendor-period invoice values" icon={<StorefrontOutlined />}>
              {vendorTrendRows.length ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={vendorTrendRows} layout="vertical" margin={{ top: 8, right: 18, left: 44, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="4 4" horizontal={false} stroke="#e2e8f0" />
                      <XAxis type="number" tickFormatter={formatCompactCurrency} tick={chartTickSx} />
                      <YAxis type="category" dataKey="label" width={180} tick={chartTickSx} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar dataKey="invoiceValue" name="Invoice Value" fill={T.green} radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : <EmptyPanel />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Unit Price Coverage" subtitle="Item price observations by vendor/date" icon={<Inventory2Outlined />}>
              {unitPriceCoverageRows.length ? (
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={unitPriceCoverageRows} margin={{ top: 10, right: 18, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="label" angle={-22} textAnchor="end" height={72} tick={chartTickSx} />
                      <YAxis yAxisId="price" tickFormatter={formatCompactCurrency} tick={chartTickSx} width={72} />
                      <YAxis yAxisId="count" orientation="right" allowDecimals={false} tick={chartTickSx} width={38} />
                      <RechartsTooltip content={<ChartTooltip />} />
                      <Bar yAxisId="price" dataKey="unitPrice" name="Unit Price" fill={T.purple} radius={[8, 8, 0, 0]} />
                      <Line yAxisId="count" dataKey="observations" name="Price Observations" stroke={T.amber} strokeWidth={3} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Box>
              ) : <EmptyPanel />}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Data Availability" subtitle="Response availability flags and reasons" icon={<InfoOutlined />}>
              <Stack spacing={1.2}>
                {availabilityRows.map((row) => (
                  <Paper key={row.metric} variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: T.border, backgroundColor: T.soft }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 13, color: T.text }}>{row.metric}</Typography>
                      <Chip size="small" color={row.status === "Available" ? "success" : "default"} label={row.status} sx={{ ...baseTextSx }} />
                    </Stack>
                    <Typography sx={{ ...baseTextSx, color: T.muted, mt: 0.5 }}>{row.detail}</Typography>
                  </Paper>
                ))}
              </Stack>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Geographic Analytics" subtitle="Country, state and city invoice value arrays when available" icon={<InfoOutlined />}>
              <Grid container spacing={2}>
                {[
                  ["Country Wise", asArray(geo.country_wise_invoice_value).length],
                  ["State Wise", asArray(geo.state_wise_invoice_value).length],
                  ["City Wise", asArray(geo.city_wise_invoice_value).length],
                ].map(([label, count]) => (
                  <Grid item xs={12} md={4} key={label}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: "center", borderColor: T.border, backgroundColor: T.soft }}>
                      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 24, color: T.text }}>{formatNumber(count)}</Typography>
                      <Typography sx={{ ...baseTextSx, color: T.muted }}>{label}</Typography>
                    </Paper>
                  </Grid>
                ))}
                {!geo.available && geo.reason ? (
                  <Grid item xs={12}>
                    <Typography sx={{ ...baseTextSx, color: T.muted }}>{geo.reason}</Typography>
                  </Grid>
                ) : null}
              </Grid>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <SectionCard title="Applied Filters And Growth" subtitle="Filters, MoM, YoY and trend direction" icon={trend.direction === "DOWNWARD" ? <TrendingDown /> : <TrendingUp />}>
              <Grid container spacing={1.2}>
                {[
                  ["MoM Current", formatCurrency(invoice.mom_growth?.current_value)],
                  ["MoM Previous", formatCurrency(invoice.mom_growth?.previous_value)],
                  ["MoM Variance", `${formatCurrency(invoice.mom_growth?.variance_value)} (${formatPercent(invoice.mom_growth?.variance_percentage)})`],
                  ["YoY Variance", `${formatCurrency(invoice.yoy_growth?.variance_value)} (${formatPercent(invoice.yoy_growth?.variance_percentage)})`],
                  ["Trend", trend.direction || "-"],
                  ["Reprocessed", overview.reprocessed_submissions_included ? "Yes" : "No"],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} key={label}>
                    <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2, borderColor: T.border, backgroundColor: T.soft }}>
                      <Typography sx={{ ...baseTextSx, color: T.muted }}>{label}</Typography>
                      <Typography sx={{ fontFamily: GILROY_BOLD, fontSize: 14, color: T.text, mt: 0.4 }}>{value}</Typography>
                    </Paper>
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(appliedFilters.length ? appliedFilters : ["No narrow filters applied"]).map((filter) => (
                      <Chip key={filter} label={filter} sx={{ fontFamily: GILROY_MEDIUM ,fontSize: 13}} />
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PayAsYouGoDashboard;
